/**
 * G2eGente - Aba de Solicitações de Mudanças
 * Gestão de solicitações de: mudança de cargo, alteração salarial, admissão e demissão
 */
import React, { useState, useEffect } from 'react';
import { changeRequestsAPI, employeesAPI, jobPositionsAPI } from '../../lib/api';
import { formatDateBR, formatDateTimeBR, formatCurrency } from '../../lib/formatters';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { 
  Plus, 
  Search, 
  Loader2,
  Eye,
  Edit,
  Trash2,
  Printer,
  CheckCircle,
  XCircle,
  Send,
  FileText,
  Briefcase,
  DollarSign,
  UserPlus,
  UserMinus,
  Clock,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import jsPDF from 'jspdf';

// Tipos de solicitação
const TIPOS_SOLICITACAO = {
  mudanca_cargo: { label: 'Mudança de Cargo', icon: Briefcase, color: 'bg-blue-100 text-blue-700' },
  alteracao_salarial: { label: 'Alteração Salarial', icon: DollarSign, color: 'bg-green-100 text-green-700' },
  admissao: { label: 'Admissão', icon: UserPlus, color: 'bg-purple-100 text-purple-700' },
  demissao: { label: 'Demissão', icon: UserMinus, color: 'bg-red-100 text-red-700' }
};

// Status
const STATUS_LABELS = {
  rascunho: { label: 'Rascunho', color: 'bg-slate-100 text-slate-700' },
  pendente: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700' },
  em_analise: { label: 'Em Análise', color: 'bg-blue-100 text-blue-700' },
  aprovado: { label: 'Aprovado', color: 'bg-green-100 text-green-700' },
  rejeitado: { label: 'Rejeitado', color: 'bg-red-100 text-red-700' },
  efetivado: { label: 'Efetivado', color: 'bg-emerald-100 text-emerald-700' },
  cancelado: { label: 'Cancelado', color: 'bg-slate-100 text-slate-600' }
};

// Tipos de mudança de cargo
const TIPOS_MUDANCA_CARGO = [
  { value: 'promocao', label: 'Promoção' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'remanejamento', label: 'Remanejamento' },
  { value: 'substituicao_temporaria', label: 'Substituição Temporária' }
];

// Tipos de alteração salarial
const TIPOS_ALTERACAO_SALARIAL = [
  { value: 'reajuste_anual', label: 'Reajuste Anual' },
  { value: 'promocao', label: 'Promoção' },
  { value: 'merito', label: 'Mérito' },
  { value: 'enquadramento', label: 'Enquadramento' },
  { value: 'dissidio', label: 'Dissídio Coletivo' }
];

// Tipos de desligamento
const TIPOS_DESLIGAMENTO = [
  { value: 'pedido', label: 'Pedido de Demissão' },
  { value: 'sem_justa_causa', label: 'Demissão Sem Justa Causa' },
  { value: 'justa_causa', label: 'Demissão Por Justa Causa' },
  { value: 'acordo', label: 'Acordo entre as partes' },
  { value: 'termino_contrato', label: 'Término de Contrato' },
  { value: 'aposentadoria', label: 'Aposentadoria' },
  { value: 'falecimento', label: 'Falecimento' }
];

// Tipos de contrato
const TIPOS_CONTRATO = [
  { value: 'clt', label: 'CLT' },
  { value: 'pj', label: 'PJ' },
  { value: 'temporario', label: 'Temporário' },
  { value: 'estagio', label: 'Estágio' },
  { value: 'jovem_aprendiz', label: 'Jovem Aprendiz' }
];

export default function ChangeRequestsTab() {
  const { user, selectedCompany } = useAuth();
  const [loading, setLoading] = useState(true);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [positions, setPositions] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  
  // Modal states
  const [showNewModal, setShowNewModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showTypeSelector, setShowTypeSelector] = useState(true);
  const [selectedSolicitacao, setSelectedSolicitacao] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formTipo, setFormTipo] = useState('');
  const [formData, setFormData] = useState({
    funcionario_id: '',
    justificativa: '',
    data_efetivacao: '',
    dados: {}
  });

  useEffect(() => {
    fetchData();
  }, [selectedCompany?.company_id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [solicitacoesRes, employeesRes, positionsRes] = await Promise.all([
        changeRequestsAPI.list(),
        employeesAPI.getAll({ situacao: 'ativo' }),
        jobPositionsAPI.getAll()
      ]);
      setSolicitacoes(solicitacoesRes.data);
      setEmployees(employeesRes.data);
      setPositions(positionsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormTipo('');
    setFormData({
      funcionario_id: '',
      justificativa: '',
      data_efetivacao: '',
      dados: {}
    });
    setShowTypeSelector(true);
  };

  const handleOpenNew = () => {
    resetForm();
    setShowNewModal(true);
  };

  const handleSelectType = (tipo) => {
    setFormTipo(tipo);
    setShowTypeSelector(false);
    setFormData({
      funcionario_id: '',
      justificativa: '',
      data_efetivacao: '',
      dados: {}
    });
  };

  const handleEmployeeSelect = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    setFormData(prev => ({
      ...prev,
      funcionario_id: employeeId,
      dados: {
        ...prev.dados,
        cargo_atual: emp?.cargo,
        departamento_atual: emp?.departamento,
        salario_atual: emp?.salario
      }
    }));
  };

  const handleSave = async (isRascunho = false) => {
    try {
      setSaving(true);
      
      // Validações básicas
      if (!formTipo) {
        toast.error('Selecione o tipo de solicitação');
        return;
      }
      
      if (formTipo !== 'admissao' && !formData.funcionario_id) {
        toast.error('Selecione o funcionário');
        return;
      }
      
      if (!formData.justificativa) {
        toast.error('Informe a justificativa');
        return;
      }
      
      const payload = {
        tipo: formTipo,
        funcionario_id: formData.funcionario_id || null,
        dados: formData.dados,
        justificativa: formData.justificativa,
        data_efetivacao: formData.data_efetivacao || null,
        rascunho: isRascunho
      };
      
      await changeRequestsAPI.create(payload);
      toast.success(isRascunho ? 'Rascunho salvo' : 'Solicitação enviada para aprovação');
      setShowNewModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar solicitação');
    } finally {
      setSaving(false);
    }
  };

  const handleViewDetail = async (solicitacao) => {
    try {
      const res = await changeRequestsAPI.getById(solicitacao.id);
      setSelectedSolicitacao(res.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleAprovar = async () => {
    if (!selectedSolicitacao) return;
    try {
      setSaving(true);
      await changeRequestsAPI.aprovar(selectedSolicitacao.id, { observacao: '' });
      toast.success('Solicitação aprovada');
      setShowDetailModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar');
    } finally {
      setSaving(false);
    }
  };

  const handleRejeitar = async () => {
    if (!selectedSolicitacao) return;
    const observacao = prompt('Informe o motivo da rejeição:');
    if (!observacao) {
      toast.error('Motivo é obrigatório para rejeição');
      return;
    }
    try {
      setSaving(true);
      await changeRequestsAPI.rejeitar(selectedSolicitacao.id, { observacao });
      toast.success('Solicitação rejeitada');
      setShowDetailModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao rejeitar');
    } finally {
      setSaving(false);
    }
  };

  const handleEfetivar = async () => {
    if (!selectedSolicitacao) return;
    if (!window.confirm('Confirma a efetivação desta solicitação? As mudanças serão aplicadas no sistema.')) return;
    try {
      setSaving(true);
      await changeRequestsAPI.efetivar(selectedSolicitacao.id);
      toast.success('Solicitação efetivada - mudanças aplicadas');
      setShowDetailModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao efetivar');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelar = async (solicitacao) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta solicitação?')) return;
    try {
      await changeRequestsAPI.cancelar(solicitacao.id, { observacao: 'Cancelado pelo usuário' });
      toast.success('Solicitação cancelada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar');
    }
  };

  const handleDelete = async (solicitacao) => {
    if (!window.confirm('Excluir definitivamente esta solicitação?\n\nEsta ação não pode ser desfeita.')) return;
    try {
      await changeRequestsAPI.delete(solicitacao.id);
      toast.success('Solicitação excluída');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const generatePDF = (solicitacao) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;
    
    // Título
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('SOLICITACAO DE MUDANCA DE PESSOAL', pageWidth / 2, y, { align: 'center' });
    y += 15;
    
    // Protocolo e Status
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Protocolo: ${solicitacao.numero}`, 20, y);
    doc.text(`Status: ${STATUS_LABELS[solicitacao.status]?.label || solicitacao.status}`, 120, y);
    y += 6;
    doc.text(`Data: ${formatDateBR(solicitacao.created_at)}`, 20, y);
    doc.text(`Tipo: ${TIPOS_SOLICITACAO[solicitacao.tipo]?.label || solicitacao.tipo}`, 120, y);
    y += 15;
    
    // Dados do funcionário
    doc.setFont('helvetica', 'bold');
    doc.text('1. DADOS DO FUNCIONARIO', 20, y);
    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.text(`Nome: ${solicitacao.funcionario_nome || solicitacao.dados?.nome || '-'}`, 20, y);
    y += 6;
    if (solicitacao.funcionario?.cpf) {
      doc.text(`CPF: ${solicitacao.funcionario.cpf}`, 20, y);
      y += 6;
    }
    if (solicitacao.funcionario?.cargo) {
      doc.text(`Cargo Atual: ${solicitacao.funcionario.cargo}`, 20, y);
      y += 6;
    }
    if (solicitacao.funcionario?.departamento) {
      doc.text(`Departamento: ${solicitacao.funcionario.departamento}`, 20, y);
      y += 6;
    }
    y += 10;
    
    // Dados da solicitação
    doc.setFont('helvetica', 'bold');
    doc.text('2. ALTERACAO SOLICITADA', 20, y);
    y += 8;
    doc.setFont('helvetica', 'normal');
    
    const dados = solicitacao.dados || {};
    if (solicitacao.tipo === 'mudanca_cargo') {
      doc.text(`Novo Cargo: ${dados.novo_cargo || '-'}`, 20, y);
      y += 6;
      doc.text(`Novo Departamento: ${dados.novo_departamento || '-'}`, 20, y);
      y += 6;
      doc.text(`Tipo de Mudanca: ${TIPOS_MUDANCA_CARGO.find(t => t.value === dados.tipo_mudanca)?.label || dados.tipo_mudanca || '-'}`, 20, y);
      y += 6;
    } else if (solicitacao.tipo === 'alteracao_salarial') {
      doc.text(`Salario Atual: R$ ${dados.salario_atual?.toFixed(2) || '-'}`, 20, y);
      y += 6;
      doc.text(`Novo Salario: R$ ${dados.novo_salario?.toFixed(2) || '-'}`, 20, y);
      y += 6;
      doc.text(`Tipo: ${TIPOS_ALTERACAO_SALARIAL.find(t => t.value === dados.tipo_alteracao)?.label || dados.tipo_alteracao || '-'}`, 20, y);
      y += 6;
    } else if (solicitacao.tipo === 'admissao') {
      doc.text(`Cargo: ${dados.cargo || '-'}`, 20, y);
      y += 6;
      doc.text(`Departamento: ${dados.departamento || '-'}`, 20, y);
      y += 6;
      doc.text(`Salario Proposto: R$ ${dados.salario?.toFixed(2) || '-'}`, 20, y);
      y += 6;
      doc.text(`Tipo Contrato: ${TIPOS_CONTRATO.find(t => t.value === dados.tipo_contrato)?.label || dados.tipo_contrato || '-'}`, 20, y);
      y += 6;
    } else if (solicitacao.tipo === 'demissao') {
      doc.text(`Tipo Desligamento: ${TIPOS_DESLIGAMENTO.find(t => t.value === dados.tipo_desligamento)?.label || dados.tipo_desligamento || '-'}`, 20, y);
      y += 6;
      doc.text(`Data Desligamento: ${dados.data_desligamento || solicitacao.data_efetivacao || '-'}`, 20, y);
      y += 6;
    }
    
    if (solicitacao.data_efetivacao) {
      doc.text(`Data de Efetivacao: ${formatDateBR(solicitacao.data_efetivacao)}`, 20, y);
      y += 6;
    }
    y += 10;
    
    // Justificativa
    doc.setFont('helvetica', 'bold');
    doc.text('3. JUSTIFICATIVA', 20, y);
    y += 8;
    doc.setFont('helvetica', 'normal');
    const justLines = doc.splitTextToSize(solicitacao.justificativa || '-', 170);
    doc.text(justLines, 20, y);
    y += justLines.length * 5 + 10;
    
    // Solicitante
    doc.setFont('helvetica', 'bold');
    doc.text('4. TRAMITACAO', 20, y);
    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.text(`Solicitante: ${solicitacao.solicitante?.nome || '-'}`, 20, y);
    y += 6;
    doc.text(`Data da Solicitacao: ${formatDateTimeBR(solicitacao.created_at)}`, 20, y);
    y += 15;
    
    // Aprovação
    if (solicitacao.aprovador) {
      doc.text(`Aprovador: ${solicitacao.aprovador.nome || '-'}`, 20, y);
      y += 6;
      doc.text(`Data: ${formatDateTimeBR(solicitacao.aprovador.data)}`, 20, y);
      y += 6;
      if (solicitacao.aprovador.observacao) {
        doc.text(`Observacao: ${solicitacao.aprovador.observacao}`, 20, y);
        y += 6;
      }
    } else {
      // Campo para assinatura
      doc.setFont('helvetica', 'bold');
      doc.text('APROVACAO', 20, y);
      y += 10;
      doc.setFont('helvetica', 'normal');
      doc.text('(  ) APROVADO    (  ) REJEITADO', 20, y);
      y += 15;
      doc.text('Observacoes: ________________________________________________', 20, y);
      y += 15;
      doc.text('Data: ___/___/______', 20, y);
      y += 15;
      doc.text('________________________________', 20, y);
      y += 6;
      doc.text('Assinatura do Aprovador', 20, y);
    }
    
    // Rodapé
    doc.setFontSize(8);
    doc.text(`Documento gerado em: ${formatDateTimeBR(new Date())} | G2eGente - Gestao de Pessoas`, pageWidth / 2, 285, { align: 'center' });
    
    doc.save(`solicitacao_${solicitacao.numero}.pdf`);
    toast.success('PDF gerado com sucesso');
  };

  // Filtrar solicitações
  const filteredSolicitacoes = solicitacoes.filter(s => {
    if (search) {
      const searchLower = search.toLowerCase();
      if (!s.numero?.toLowerCase().includes(searchLower) &&
          !s.funcionario_nome?.toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    if (filterStatus && s.status !== filterStatus) return false;
    if (filterTipo && s.tipo !== filterTipo) return false;
    return true;
  });

  // Stats
  const stats = {
    pendentes: solicitacoes.filter(s => s.status === 'pendente').length,
    aprovadas: solicitacoes.filter(s => s.status === 'aprovado').length,
    rejeitadas: solicitacoes.filter(s => s.status === 'rejeitado').length,
    efetivadas: solicitacoes.filter(s => s.status === 'efetivado').length
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold text-yellow-700">{stats.pendentes}</p>
                <p className="text-xs text-yellow-600">Pendentes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.aprovadas}</p>
                <p className="text-xs text-green-600">Aprovadas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <XCircle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-700">{stats.rejeitadas}</p>
                <p className="text-xs text-red-600">Rejeitadas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold text-emerald-700">{stats.efetivadas}</p>
                <p className="text-xs text-emerald-600">Efetivadas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros e Ações */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Solicitacoes de Mudancas</CardTitle>
              <CardDescription>Gerencie solicitacoes de alteracoes de pessoal</CardDescription>
            </div>
            <Button onClick={handleOpenNew} data-testid="new-change-request-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nova Solicitacao
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Buscar por numero ou funcionario..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterTipo || 'all'} onValueChange={(v) => setFilterTipo(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                {Object.entries(TIPOS_SOLICITACAO).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                {Object.entries(STATUS_LABELS).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tabela */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N</TableHead>
                <TableHead>Funcionario</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Solicitante</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSolicitacoes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    Nenhuma solicitacao encontrada
                  </TableCell>
                </TableRow>
              ) : (
                filteredSolicitacoes.map((sol) => {
                  const TipoIcon = TIPOS_SOLICITACAO[sol.tipo]?.icon || FileText;
                  return (
                    <TableRow key={sol.id}>
                      <TableCell className="font-mono text-sm">{sol.numero}</TableCell>
                      <TableCell className="font-medium">
                        {sol.funcionario_nome || sol.dados?.nome || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={TIPOS_SOLICITACAO[sol.tipo]?.color}>
                          <TipoIcon className="w-3 h-3 mr-1" />
                          {TIPOS_SOLICITACAO[sol.tipo]?.label || sol.tipo}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {sol.solicitante?.nome || '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDateBR(sol.created_at)}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_LABELS[sol.status]?.color}>
                          {STATUS_LABELS[sol.status]?.label || sol.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetail(sol)}
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => generatePDF(sol)}
                            title="Imprimir"
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                          {['rascunho', 'pendente', 'aprovado'].includes(sol.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(sol)}
                              title="Excluir"
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-solicitacao-${sol.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                          {['rascunho', 'pendente'].includes(sol.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancelar(sol)}
                              title="Cancelar"
                              className="text-orange-600 hover:text-orange-700"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal Nova Solicitação */}
      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {showTypeSelector ? 'Nova Solicitacao de Mudanca' : `Solicitacao de ${TIPOS_SOLICITACAO[formTipo]?.label}`}
            </DialogTitle>
            <DialogDescription>
              {showTypeSelector ? 'Selecione o tipo de solicitacao' : 'Preencha os dados da solicitacao'}
            </DialogDescription>
          </DialogHeader>

          {showTypeSelector ? (
            <div className="grid grid-cols-2 gap-4 py-4">
              {Object.entries(TIPOS_SOLICITACAO).map(([key, val]) => {
                const Icon = val.icon;
                return (
                  <Button
                    key={key}
                    variant="outline"
                    className="h-24 flex flex-col gap-2 hover:bg-slate-50"
                    onClick={() => handleSelectType(key)}
                    data-testid={`select-type-${key}`}
                  >
                    <Icon className="w-8 h-8" />
                    <span>{val.label}</span>
                  </Button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Seleção de Funcionário (exceto admissão) */}
              {formTipo !== 'admissao' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800">Dados do Funcionario</h3>
                  <div>
                    <Label>Funcionario *</Label>
                    <Select value={formData.funcionario_id} onValueChange={handleEmployeeSelect}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o funcionario..." />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.nome} - {emp.cargo || 'Sem cargo'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.funcionario_id && (
                    <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg text-sm">
                      <div>
                        <span className="text-slate-500">Cargo atual:</span>
                        <p className="font-medium">{formData.dados.cargo_atual || '-'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Departamento:</span>
                        <p className="font-medium">{formData.dados.departamento_atual || '-'}</p>
                      </div>
                      {formTipo === 'alteracao_salarial' && (
                        <div>
                          <span className="text-slate-500">Salario atual:</span>
                          <p className="font-medium">R$ {formData.dados.salario_atual?.toFixed(2) || '-'}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Campos específicos por tipo */}
              {formTipo === 'mudanca_cargo' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800">Dados da Mudanca</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Novo Cargo *</Label>
                      <Select
                        value={formData.dados.novo_cargo || ''}
                        onValueChange={(v) => setFormData(prev => ({
                          ...prev,
                          dados: { ...prev.dados, novo_cargo: v }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {positions.map(pos => (
                            <SelectItem key={pos.id} value={pos.titulo}>{pos.titulo}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Novo Departamento</Label>
                      <Input
                        value={formData.dados.novo_departamento || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          dados: { ...prev.dados, novo_departamento: e.target.value }
                        }))}
                        placeholder="Departamento"
                      />
                    </div>
                    <div>
                      <Label>Tipo de Mudanca *</Label>
                      <Select
                        value={formData.dados.tipo_mudanca || ''}
                        onValueChange={(v) => setFormData(prev => ({
                          ...prev,
                          dados: { ...prev.dados, tipo_mudanca: v }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {TIPOS_MUDANCA_CARGO.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {formTipo === 'alteracao_salarial' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800">Dados Salariais</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Novo Salario *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.dados.novo_salario || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          dados: { ...prev.dados, novo_salario: parseFloat(e.target.value) || 0 }
                        }))}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label>Tipo de Alteracao *</Label>
                      <Select
                        value={formData.dados.tipo_alteracao || ''}
                        onValueChange={(v) => setFormData(prev => ({
                          ...prev,
                          dados: { ...prev.dados, tipo_alteracao: v }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {TIPOS_ALTERACAO_SALARIAL.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {formData.dados.novo_salario && formData.dados.salario_atual && (
                    <div className="p-3 bg-blue-50 rounded-lg text-sm">
                      <span className="text-blue-600">Diferenca: </span>
                      <span className="font-bold text-blue-800">
                        R$ {(formData.dados.novo_salario - formData.dados.salario_atual).toFixed(2)} 
                        ({((formData.dados.novo_salario / formData.dados.salario_atual - 1) * 100).toFixed(2)}%)
                      </span>
                    </div>
                  )}
                </div>
              )}

              {formTipo === 'admissao' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800">Dados do Candidato</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Nome Completo *</Label>
                      <Input
                        value={formData.dados.nome || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          dados: { ...prev.dados, nome: e.target.value }
                        }))}
                        placeholder="Nome completo"
                      />
                    </div>
                    <div>
                      <Label>CPF *</Label>
                      <Input
                        value={formData.dados.cpf || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          dados: { ...prev.dados, cpf: e.target.value }
                        }))}
                        placeholder="000.000.000-00"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={formData.dados.email || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          dados: { ...prev.dados, email: e.target.value }
                        }))}
                        placeholder="email@exemplo.com"
                      />
                    </div>
                  </div>
                  <h3 className="font-semibold text-slate-800 pt-4">Dados da Contratacao</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Cargo *</Label>
                      <Select
                        value={formData.dados.cargo || ''}
                        onValueChange={(v) => setFormData(prev => ({
                          ...prev,
                          dados: { ...prev.dados, cargo: v }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {positions.map(pos => (
                            <SelectItem key={pos.id} value={pos.titulo}>{pos.titulo}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Departamento *</Label>
                      <Input
                        value={formData.dados.departamento || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          dados: { ...prev.dados, departamento: e.target.value }
                        }))}
                        placeholder="Departamento"
                      />
                    </div>
                    <div>
                      <Label>Salario Proposto *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.dados.salario || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          dados: { ...prev.dados, salario: parseFloat(e.target.value) || 0 }
                        }))}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label>Tipo de Contrato *</Label>
                      <Select
                        value={formData.dados.tipo_contrato || ''}
                        onValueChange={(v) => setFormData(prev => ({
                          ...prev,
                          dados: { ...prev.dados, tipo_contrato: v }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {TIPOS_CONTRATO.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {formTipo === 'demissao' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800">Dados do Desligamento</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Tipo de Desligamento *</Label>
                      <Select
                        value={formData.dados.tipo_desligamento || ''}
                        onValueChange={(v) => setFormData(prev => ({
                          ...prev,
                          dados: { ...prev.dados, tipo_desligamento: v }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {TIPOS_DESLIGAMENTO.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Data do Desligamento *</Label>
                      <Input
                        type="date"
                        value={formData.dados.data_desligamento || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          dados: { ...prev.dados, data_desligamento: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <Label>Ultimo Dia de Trabalho</Label>
                      <Input
                        type="date"
                        value={formData.dados.ultimo_dia_trabalho || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          dados: { ...prev.dados, ultimo_dia_trabalho: e.target.value }
                        }))}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Data de Efetivação e Justificativa */}
              <div className="space-y-4 pt-4 border-t">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Data de Efetivacao</Label>
                    <Input
                      type="date"
                      value={formData.data_efetivacao}
                      onChange={(e) => setFormData(prev => ({ ...prev, data_efetivacao: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <Label>Justificativa *</Label>
                  <Textarea
                    value={formData.justificativa}
                    onChange={(e) => setFormData(prev => ({ ...prev, justificativa: e.target.value }))}
                    placeholder="Descreva detalhadamente o motivo desta solicitacao..."
                    rows={4}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {!showTypeSelector && (
              <Button variant="outline" onClick={() => setShowTypeSelector(true)}>
                Voltar
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowNewModal(false)}>
              Cancelar
            </Button>
            {!showTypeSelector && (
              <>
                <Button variant="secondary" onClick={() => handleSave(true)} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Salvar Rascunho
                </Button>
                <Button onClick={() => handleSave(false)} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Send className="w-4 h-4 mr-2" />
                  Enviar para Aprovacao
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Detalhes / Aprovação */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedSolicitacao && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle>Solicitacao {selectedSolicitacao.numero}</DialogTitle>
                  <Badge className={STATUS_LABELS[selectedSolicitacao.status]?.color}>
                    {STATUS_LABELS[selectedSolicitacao.status]?.label}
                  </Badge>
                </div>
                <DialogDescription>
                  {TIPOS_SOLICITACAO[selectedSolicitacao.tipo]?.label} - 
                  Criada em {formatDateBR(selectedSolicitacao.created_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Dados do Funcionário */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Dados do Funcionario</h3>
                  <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                    <div>
                      <span className="text-xs text-slate-500">Nome</span>
                      <p className="font-medium">{selectedSolicitacao.funcionario?.nome || selectedSolicitacao.funcionario_nome || selectedSolicitacao.dados?.nome || '-'}</p>
                    </div>
                    {selectedSolicitacao.funcionario?.cpf && (
                      <div>
                        <span className="text-xs text-slate-500">CPF</span>
                        <p className="font-medium">{selectedSolicitacao.funcionario.cpf}</p>
                      </div>
                    )}
                    {selectedSolicitacao.funcionario?.cargo && (
                      <div>
                        <span className="text-xs text-slate-500">Cargo Atual</span>
                        <p className="font-medium">{selectedSolicitacao.funcionario.cargo}</p>
                      </div>
                    )}
                    {selectedSolicitacao.funcionario?.departamento && (
                      <div>
                        <span className="text-xs text-slate-500">Departamento</span>
                        <p className="font-medium">{selectedSolicitacao.funcionario.departamento}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Dados da Mudança */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Alteracao Solicitada</h3>
                  <div className="p-4 bg-blue-50 rounded-lg space-y-3">
                    {selectedSolicitacao.tipo === 'mudanca_cargo' && (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-600">{selectedSolicitacao.funcionario?.cargo || '-'}</span>
                          <ArrowRight className="w-4 h-4 text-blue-600" />
                          <span className="font-semibold text-blue-800">{selectedSolicitacao.dados?.novo_cargo || '-'}</span>
                        </div>
                        <p className="text-sm text-slate-600">
                          Tipo: {TIPOS_MUDANCA_CARGO.find(t => t.value === selectedSolicitacao.dados?.tipo_mudanca)?.label || '-'}
                        </p>
                      </>
                    )}
                    {selectedSolicitacao.tipo === 'alteracao_salarial' && (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-600">R$ {selectedSolicitacao.dados?.salario_atual?.toFixed(2) || '-'}</span>
                          <ArrowRight className="w-4 h-4 text-green-600" />
                          <span className="font-semibold text-green-800">R$ {selectedSolicitacao.dados?.novo_salario?.toFixed(2) || '-'}</span>
                        </div>
                        <p className="text-sm text-slate-600">
                          Tipo: {TIPOS_ALTERACAO_SALARIAL.find(t => t.value === selectedSolicitacao.dados?.tipo_alteracao)?.label || '-'}
                        </p>
                      </>
                    )}
                    {selectedSolicitacao.tipo === 'admissao' && (
                      <>
                        <p><strong>Cargo:</strong> {selectedSolicitacao.dados?.cargo}</p>
                        <p><strong>Departamento:</strong> {selectedSolicitacao.dados?.departamento}</p>
                        <p><strong>Salario:</strong> R$ {selectedSolicitacao.dados?.salario?.toFixed(2)}</p>
                        <p><strong>Contrato:</strong> {TIPOS_CONTRATO.find(t => t.value === selectedSolicitacao.dados?.tipo_contrato)?.label}</p>
                      </>
                    )}
                    {selectedSolicitacao.tipo === 'demissao' && (
                      <>
                        <p><strong>Tipo:</strong> {TIPOS_DESLIGAMENTO.find(t => t.value === selectedSolicitacao.dados?.tipo_desligamento)?.label}</p>
                        <p><strong>Data:</strong> {selectedSolicitacao.dados?.data_desligamento}</p>
                      </>
                    )}
                    {selectedSolicitacao.data_efetivacao && (
                      <p className="text-sm"><strong>Data de Efetivacao:</strong> {formatDateBR(selectedSolicitacao.data_efetivacao)}</p>
                    )}
                  </div>
                </div>

                {/* Justificativa */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Justificativa</h3>
                  <p className="text-slate-700 p-4 bg-slate-50 rounded-lg whitespace-pre-wrap">
                    {selectedSolicitacao.justificativa || '-'}
                  </p>
                </div>

                {/* Tramitação */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Tramitacao</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Solicitante:</strong> {selectedSolicitacao.solicitante?.nome} - {formatDateTimeBR(selectedSolicitacao.created_at)}</p>
                    {selectedSolicitacao.aprovador && (
                      <>
                        <p><strong>Aprovador:</strong> {selectedSolicitacao.aprovador.nome} - {formatDateTimeBR(selectedSolicitacao.aprovador.data)}</p>
                        {selectedSolicitacao.aprovador.observacao && (
                          <p className="text-slate-600"><strong>Obs:</strong> {selectedSolicitacao.aprovador.observacao}</p>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Histórico */}
                {selectedSolicitacao.historico?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-3">Historico</h3>
                    <div className="space-y-2 text-sm border-l-2 border-slate-200 pl-4">
                      {selectedSolicitacao.historico.map((h, idx) => (
                        <div key={idx} className="pb-2">
                          <p className="font-medium capitalize">{h.acao}</p>
                          <p className="text-slate-500 text-xs">
                            {h.user_nome} - {formatDateTimeBR(h.data)}
                          </p>
                          {h.observacao && <p className="text-slate-600 mt-1">{h.observacao}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="gap-2 flex-wrap">
                <Button variant="outline" onClick={() => generatePDF(selectedSolicitacao)}>
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir
                </Button>
                
                {selectedSolicitacao.status === 'pendente' && (
                  <>
                    <Button variant="destructive" onClick={handleRejeitar} disabled={saving}>
                      <XCircle className="w-4 h-4 mr-2" />
                      Rejeitar
                    </Button>
                    <Button className="bg-green-600 hover:bg-green-700" onClick={handleAprovar} disabled={saving}>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Aprovar
                    </Button>
                  </>
                )}
                
                {selectedSolicitacao.status === 'aprovado' && (
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleEfetivar} disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Efetivar Mudancas
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
